import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Typography } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import LoaderContainer from "../../components/LoaderContainer";
import CustomButton from "../../components/Custom/CustomButton";
import { useTranslation } from "react-i18next";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomCard from "../../components/Famer/GenericCard";
import ConfirmationPopup from "../../components/Popup/GenericPopup";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import { getAuth, saveAuth, saveToLocal } from "../../globals/authentication";

export default function AreaManagerList() {
  const [areaManagers, setAreaManagers] = useState([]);
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  useEffect(() => {
    fetchAreaManagers();
  }, []);

  const fetchAreaManagers = async () => {
    try {
      const response = await api.get("/popmanager/get-area-managers-list/");
      setAreaManagers(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching area managers:", error);
      setLoading(false);
      showAlert("error", "Error fetching Area Managers");
    }
  };

  const handleDelete = (id) => {
    setSelectedManagerId(id);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/auth/delete-account/?user_id=${selectedManagerId}`);
      showAlert("success", "Area Manager Deleted Successfully");
      fetchAreaManagers();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Error deleting the Area Manager";
      showAlert("error", errorMessage);
    }
    setDeleteConfirmationOpen(false);
  };

  const handleEditClick = (managerId) => {
    navigate(`/popmanager/area-managers/edit/?managerId=${managerId}`);
  };

  const handleAddAreaManager = () => {
    navigate("/pop-manager/area-manager/add");
  };

  const handleLoginAreaManager = async (id) => {
    try {
      // Backup current auth data
      const currentAuthData = getAuth();
      if (currentAuthData) {
        saveToLocal("auth_backup", currentAuthData);
      }

      const response = await api.post(
        `/popmanager/login-area-manager/?persona_user=${id}`
      );

      const authData = JSON.stringify(response.data);
      saveAuth(authData);
      navigate(`/login-internal/?authDetails=${authData}`, { replace: true });
    } catch (error) {
      console.error("Error logging in as Area Manager:", error);
      showAlert("error", "Error logging in as Area Manager");
    }
  };

  return (
    <>
      <Helmet>
        <title>My Area Managers - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container
          maxWidth="xs"
          style={{
            height: areaManagers.length === 0 ? "58vh" : "94vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {areaManagers.length !== 0 && (
            <CustomTitle>{t("My Area Managers")}</CustomTitle>
          )}
          <div
            style={{
              overflowY: "auto",
              maxHeight: "calc(115vh - 300px)",
              textAlign: "center",
              height: "700px",
              margin: "auto",
            }}
          >
            {areaManagers.map((manager) => (
              <CustomCard
                key={manager.id}
                id={manager.id}
                t={t}
                data={{
                  [t("Name")]: manager.full_name,
                  [t("Username")]: manager.username,
                  [t("Email")]: manager.email,
                  [t("Phone")]: manager.phone_number,
                  [t("Address")]: manager.address,
                }}
                handleEditClick={() => handleEditClick(manager.id)}
                handleDelete={() => handleDelete(manager.id)}
                handlePrimaryButton={() => handleLoginAreaManager(manager.id)}
                primaryLabel={t("Login")}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <CustomButton onClick={handleAddAreaManager}>
              {t("Add Area Manager")}
            </CustomButton>
            {areaManagers.length === 0 && (
              <Typography variant="subtitle1" sx={{ textAlign: "center" }}>
                {t("Your Area Managers will be shown here once you add them.")}
              </Typography>
            )}
          </div>
        </Container>
      </LoaderContainer>
      <ConfirmationPopup
        open={deleteConfirmationOpen}
        handleClose={() => setDeleteConfirmationOpen(false)}
        handleConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this Area Manager?"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}
